import React, { useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import ContactModal from "../components/ContactModal";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  Calendar, 
  CheckCircle, 
  ArrowRight, 
  Users, 
  Target, 
  MessageSquare, 
  Shield,
  Award,
  Lightbulb,
  TrendingUp,
  Eye,
  Brain,
  Handshake,
  FileText,
  BarChart3
} from "lucide-react";

const seoConfig = {
  title: "KI-Coaching für C-Level | Executive AI Training | BROVKO",
  description: "Individuelles KI-Coaching für Geschäftsführung und Führungskräfte. Strategische KI-Kompetenz aufbauen, Technologien bewerten und Change Management meistern.",
  keywords: "KI-Coaching, Executive AI Training, C-Level Coaching, KI-Führungskräfte, KI-Strategie, Change Management KI",
  canonicalUrl: "/leistungsangebot/ki-coaching"
};

const KICoaching = () => {
  const { t } = useLanguage();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  // Modal handlers
  const openContactModal = (buttonContext = null, buttonText = null) => {
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };

  const coachingServices = [
    {
      icon: Users,
      title: "1:1-KI-Coaching für Geschäftsführung",
      subtitle: "Individuelle Begleitung auf C-Level",
      features: [
        "Strategische Einordnung von KI-Technologien und -Trends",
        "Bewertung und Auswahl zukunftsfähiger KI-Lösungen",
        "Organisationsentwicklung für KI-getriebene Unternehmen",
        "Toolauswahl und Technologie-Roadmap",
        "Umgang mit interner Skepsis und Change Management",
        "Persönliche Führungsstrategien für die KI-Ära"
      ],
      duration: "Monatlich oder quartalsweise",
      format: "Persönlich, vertraulich, wirkungsorientiert"
    },
    {
      icon: Shield,
      title: "AI-Beirat & AI Committee",
      subtitle: "Strategische Governance und Aufsicht",
      features: [
        "Strukturierte Begleitung der KI-Strategie",
        "Kritische Bewertung und Richtungsweisung",
        "Board-Reportings und KPI-Controlling",
        "Technologiebewertungen und Due Diligence",
        "Risikomanagement und Compliance-Überwachung",
        "Innovation und Zukunftstrends identifizieren"
      ],
      duration: "Quartalsweise oder nach Bedarf",
      format: "Beiratssitzungen, Workshops, Strategiesessions"
    }
  ];

  const challenges = [
    {
      icon: Target,
      question: "Was ist der richtige nächste Schritt?",
      solution: "Strukturierte Roadmap-Entwicklung mit priorisierten Maßnahmen"
    },
    {
      icon: TrendingUp,
      question: "Welche Technologien sind zukunftsfähig?",
      solution: "Technologie-Assessment und Marktanalyse durch Experten"
    },
    {
      icon: Users,
      question: "Wie organisieren wir die Umsetzung?",
      solution: "Organisationsentwicklung und Change Management Begleitung"
    },
    {
      icon: Handshake,
      question: "Wer übernimmt Verantwortung?",
      solution: "Klare Governance-Strukturen und Verantwortlichkeiten definieren"
    }
  ];

  const benefits = [
    {
      icon: Brain,
      title: "Praxiserprobte Werkzeuge & Modelle",
      description: "Zugriff auf bewährte KI-Toolboxen, Frameworks und Methoden aus 100+ erfolgreichen Projekten."
    },
    {
      icon: Award,
      title: "Erfahrene KI-Strategen",
      description: "Begleitung durch Top-Experten mit langjähriger Erfahrung in KI-Strategie und -Umsetzung."
    },
    {
      icon: TrendingUp,
      title: "Schneller Kompetenzaufbau",
      description: "Effiziente Wissensvermittlung für interne Teams und externe Stakeholder."
    },
    {
      icon: Eye,
      title: "Unabhängiger Außenblick",
      description: "Objektive Bewertung abgestimmt auf Ihre Branche und Unternehmensgröße."
    }
  ];

  const coachingTopics = [
    {
      category: "Strategische Führung",
      topics: [
        "KI-Vision und strategische Positionierung",
        "Digitale Transformation Leadership",
        "KI-Governance und Risikomanagement",
        "Board-Kommunikation zu KI-Themen"
      ]
    },
    {
      category: "Technologie & Innovation",
      topics: [
        "Emerging AI Technologies Assessment",
        "Make-or-Buy Entscheidungen bei KI-Tools",
        "KI-Vendor Management und Partnerships",
        "Innovation Pipeline Development"
      ]
    },
    {
      category: "Organisation & Menschen",
      topics: [
        "KI-Culture und Mindset Transformation",
        "Skills Development und Recruiting",
        "Change Management bei KI-Einführung",
        "Stakeholder Alignment und Communication"
      ]
    },
    {
      category: "Umsetzung & Performance",
      topics: [
        "KI-Projektportfolio Management",
        "ROI-Messung und KPI-Entwicklung",
        "Agile KI-Entwicklung und Skalierung",
        "Performance Monitoring und Optimization"
      ]
    }
  ];

  const targetAudience = [
    {
      role: "CEOs & Geschäftsführer",
      challenges: "Strategische KI-Positionierung, Board-Kommunikation, Transformation Leadership",
      value: "Klarheit über KI-Strategie und konkrete Handlungsschritte"
    },
    {
      role: "CTOs & CDOs",
      challenges: "Technologie-Roadmap, Architecture Decisions, Team Building",
      value: "Technische Expertise und Best Practices aus anderen Unternehmen"
    },
    {
      role: "Board Members",
      challenges: "KI-Governance, Risk Assessment, Strategic Oversight",
      value: "Unabhängige Bewertung und strukturierte Entscheidungsgrundlagen"
    }
  ];

  const whyBrovko = [
    "Exklusive C-Level Expertise aus 100+ KI-Transformationsprojekten",
    "Vertrauliche und vertrauensvolle Sparringspartnerschaft",
    "Praktische Umsetzungserfahrung, nicht nur Theorie",
    "Branchenübergreifende Perspektive und Best Practices",
    "Langfristige strategische Begleitung mit messbaren Erfolgen"
  ];

  return (
    <div className="theme-brand bg-surface text-ink min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      <SEOHead {...seoConfig} />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-40 pb-10">
        <div className="container mx-auto px-4">
          <div className="max-w-[46rem]">
            <h1 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-4 leading-tight">
              {t('kiCoaching.hero.title')}
            </h1>
            <p className="text-base text-ink-muted mb-4 leading-relaxed">
              {t('kiCoaching.hero.description')}
            </p>
            <p className="text-sm text-ink-faint mb-6">
              {t('kiCoaching.hero.subtitle')}
            </p>
            <Button 
              className="bg-teal-400 hover:bg-brand text-ink-onBrand px-6 py-3 text-sm font-semibold"
              onClick={() => openContactModal("Hero Section", t('kiCoaching.hero.cta'))}
            >
              <Calendar className="mr-2 h-4 w-4" />
              {t('kiCoaching.hero.cta')}
            </Button>
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="py-20 md:py-28 bg-surface-raised/50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-3 text-center">
              {t('kiCoaching.challenges.title')}
            </h2>
            <p className="text-base text-ink-faint mb-6 text-center">
              {t('kiCoaching.challenges.subtitle')}
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {challenges.map((challenge, index) => {
                const IconComponent = challenge.icon;
                return (
                  <div key={index} className="bg-surface-raised/50 rounded-lg p-4 border border-slate-700/30">
                    <div className="flex items-start gap-3">
                      <div className="bg-teal-400/10 p-2 rounded-lg flex-shrink-0">
                        <IconComponent className="h-5 w-5 text-brand-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold text-sm mb-1">{challenge.question}</h3>
                        <p className="text-ink-muted text-xs leading-relaxed">{challenge.solution}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Why Brovko Section */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-6 text-center">
              {t('kiCoaching.whyBrovko.title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {whyBrovko.map((item, index) => (
                <div key={index} className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-brand-400 mt-0.5 flex-shrink-0" />
                  <p className="text-ink-muted text-sm leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Coaching Services */}
      <section className="py-20 bg-slate-800/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-6 text-center">
              {t('kiCoaching.services.title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {coachingServices.map((service, index) => {
                const IconComponent = service.icon;
                return (
                  <div key={index} className="bg-surface-raised/50 backdrop-blur-sm rounded-xl p-5 border border-hairline hover:border-teal-400/50 transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-teal-400/10 p-2 rounded-lg">
                        <IconComponent className="h-6 w-6 text-brand-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white">{service.title}</h3>
                        <p className="text-ink-faint text-xs mt-0.5">{service.subtitle}</p>
                      </div>
                    </div>
                    
                    <ul className="space-y-2 mb-4">
                      {service.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-brand-400 mt-0.5 flex-shrink-0" />
                          <span className="text-ink-muted text-xs leading-relaxed">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <div className="border-t border-hairline pt-3 space-y-1.5">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-3 w-3 text-brand-400" />
                        <span className="text-brand-400 text-xs font-medium">{service.duration}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <FileText className="h-3 w-3 text-brand-400" />
                        <span className="text-brand-400 text-xs font-medium">{service.format}</span>
                      </div>
                    </div>
                    
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-teal-400 text-brand-400 hover:bg-teal-400 hover:text-ink-onBrand mt-4 w-full"
                      onClick={() => openContactModal("Coaching Services", "Erstgespräch buchen")}
                    >
                      Erstgespräch buchen
                      <ArrowRight className="ml-2 h-3 w-3" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-6 text-center">
              {t('kiCoaching.benefits.title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <div key={index} className="flex items-start gap-3">
                    <div className="bg-teal-400/10 p-2 rounded-lg flex-shrink-0">
                      <IconComponent className="h-6 w-6 text-brand-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white mb-1">{benefit.title}</h3>
                      <p className="text-ink-muted text-sm leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Coaching Topics */}
      <section className="py-20 md:py-28 bg-surface-raised/50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-6 text-center">
              {t('kiCoaching.topics.title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {coachingTopics.map((category, index) => (
                <div key={index} className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/30">
                  <h3 className="text-base font-semibold text-white mb-3">{category.category}</h3>
                  <ul className="space-y-1.5">
                    {category.topics.map((topic, tIndex) => (
                      <li key={tIndex} className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-brand-400 mt-0.5 flex-shrink-0" />
                        <span className="text-ink-muted text-xs leading-relaxed">{topic}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-3 text-center">
              {t('kiCoaching.audience.title')}
            </h2>
            <p className="text-base text-ink-faint mb-6 text-center">
              {t('kiCoaching.audience.subtitle')}
            </p>
            <div className="space-y-3">
              {targetAudience.map((audience, index) => (
                <div key={index} className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/30">
                  <div className="grid md:grid-cols-3 gap-3 items-center">
                    <h3 className="text-base font-semibold text-white">{audience.role}</h3>
                    <p className="text-ink-muted text-xs">{audience.challenges}</p>
                    <p className="text-brand-400 text-xs font-medium">{audience.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-surface-raised/50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-3">
              {t('kiCoaching.cta.title')}
            </h2>
            <p className="text-base text-ink-muted mb-6">
              {t('kiCoaching.cta.description')}
            </p>
            <div className="flex justify-center">
              <Button 
                className="bg-teal-400 hover:bg-brand text-ink-onBrand px-6 py-3 text-sm font-semibold"
                onClick={() => openContactModal("CTA Section", t('kiCoaching.hero.cta'))}
              >
                <Calendar className="mr-2 h-4 w-4" />
                {t('kiCoaching.hero.cta')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={closeContactModal}
        pageSource="KI-Coaching"
        buttonContext="KI-Coaching Service"
        buttonText="Erstgespräch buchen"
      />
    </div>
  );
};

export default KICoaching;